'use client';

import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { ensureAmplifyConfigured } from '@/layers/presentation/utils/ensureAmplifyConfigured';
import logger from '@/layers/support/monitoring/logger';
import { UserType } from '@/types/modelEnums';
import { usePathname, useRouter } from 'next/navigation';
import { PROMO_BANNER_NAVBAR_OFFSET_CLASS } from '../../hooks';
import { useOnboardingContext } from '../../providers/OnboardingProvider';
import NavbarRenderer from './NavbarRenderer';
import { getNavbarAuthPath, isNavbarHrefActive, NavbarVariant } from './navbarShared';

interface NavbarProps {
  variant?: NavbarVariant;
}

const Navbar: React.FC<NavbarProps> = ({ variant = 'default' }) => {
  const { t } = useTranslation();
  const { user, setUser, setProfile } = useGlobalState();
  const pathname = usePathname();
  const router = useRouter();

  // Use onboarding context for banner visibility instead of duplicating logic
  const { shouldShowTopBanner } = useOnboardingContext();

  const authPath = getNavbarAuthPath(pathname);

  const handleDirectLogout = async () => {
    try {
      await ensureAmplifyConfigured();
      const { signOut } = await import('aws-amplify/auth');
      await signOut();
      setUser(null);
      setProfile(null);
      router.push('/');
    } catch (error) {
      logger.error('Error during logout:', error);
    }
  };

  const menuItems = [
    ...((user?.type === UserType.PROVIDER && [
      {
        href: '/provider',
        label: t('components:navbar.provider.main'),
        isActive: isNavbarHrefActive(pathname, '/provider'),
      },
      {
        href: '/provider/programs',
        label: t('components:navbar.provider.searchPrograms'),
        isActive: isNavbarHrefActive(pathname, '/provider/programs'),
      },
      {
        href: '/provider/patients',
        label: t('components:navbar.provider.patients'),
        isActive: isNavbarHrefActive(pathname, '/provider/patients'),
      },
      {
        href: '/provider/saved-programs',
        label: t('components:navbar.provider.savedPrograms'),
        isActive: isNavbarHrefActive(pathname, '/provider/saved-programs'),
      },
    ]) ||
      []),
    ...((user?.type === UserType.SPONSOR && [
      {
        href: '/sponsor',
        label: t('components:navbar.sponsor.main'),
        isActive: isNavbarHrefActive(pathname, '/sponsor'),
      },
      {
        href: '/sponsor/programs',
        label: t('components:navbar.sponsor.myPrograms'),
        isActive: isNavbarHrefActive(pathname, '/sponsor/programs'),
      },
      {
        href: '/sponsor/programRequests',
        label: t('components:navbar.sponsor.programRequests'),
        isActive: isNavbarHrefActive(pathname, '/sponsor/programRequests'),
      },
      {
        href: '/sponsor/reports',
        label: t('components:navbar.sponsor.reports'),
        isActive: isNavbarHrefActive(pathname, '/sponsor/reports'),
      },
    ]) ||
      []),
    ...((user?.type === UserType.PATIENT && [
      {
        href: '/patient',
        label: t('components:navbar.patient.main'),
        isActive: isNavbarHrefActive(pathname, '/patient'),
      },
      {
        href: '/patient/programs',
        label: t('components:navbar.patient.myPrograms'),
        isActive: isNavbarHrefActive(pathname, '/patient/programs'),
      },
      {
        href: '/patient/saved-programs',
        label: t('components:navbar.patient.savedPrograms'),
        isActive: isNavbarHrefActive(pathname, '/patient/saved-programs'),
      },
    ]) ||
      []),
    ...((!user && [
      {
        href: '/about/patients',
        label: t('components:navbar.about.forPatients'),
        isActive: isNavbarHrefActive(pathname, '/about/patients'),
      },
      {
        href: '/about/providers',
        label: t('components:navbar.about.forProviders'),
        isActive: isNavbarHrefActive(pathname, '/about/providers'),
      },
      {
        href: '/about/sponsors',
        label: t('components:navbar.about.forSponsors'),
        isActive: isNavbarHrefActive(pathname, '/about/sponsors'),
      },
      {
        href: '/about/about-us',
        label: t('components:navbar.about.aboutUs'),
        isActive: isNavbarHrefActive(pathname, '/about/about-us'),
      },
      {
        href: '/about/faq',
        label: t('components:navbar.about.faq'),
        isActive: isNavbarHrefActive(pathname, '/about/faq'),
      },
      {
        href: '/about/contact-us',
        label: t('components:navbar.about.contactUs'),
        isActive: isNavbarHrefActive(pathname, '/about/contact-us'),
      },
    ]) ||
      []),
    {
      href: '/blog',
      label: t('components:navbar.blog'),
      isActive: isNavbarHrefActive(pathname, '/blog'),
    },
  ];

  return (
    <NavbarRenderer
      variant={variant}
      topClassName={shouldShowTopBanner ? PROMO_BANNER_NAVBAR_OFFSET_CLASS : 'top-0'}
      menuItems={menuItems}
      authHref={authPath}
      labels={{
        loginOrSignUp: 'Login or Sign Up',
        loginOrCreateAccount: 'Login or Create an Account',
        logout: 'Logout',
        myProfile: t('components:navbar.myProfile'),
      }}
      user={user}
      onLogout={handleDirectLogout}
    />
  );
};

export default Navbar;
